import { useEffect, useRef, useState } from 'react';

export interface FilterOption {
  value: string;
  label: string;
}

/**
 * Multi-value filter control (Noya "policies" pattern) built on plain CSS:
 * a trigger button that opens a popover with an internal search box, a
 * "select all" toggle, selected chips, and a checkbox list. Closes on outside
 * click / Escape.
 */
export function MultiSelectFilter({
  options,
  selected,
  onChange,
  placeholder = 'הכל',
  searchPlaceholder = 'הקלד לחיפוש…',
}: {
  options: FilterOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 40);
      return () => clearTimeout(t);
    }
    setSearch('');
  }, [open]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const filtered = search
    ? options.filter(
        (o) =>
          o.label.toLowerCase().includes(search.toLowerCase()) ||
          o.value.toLowerCase().includes(search.toLowerCase()),
      )
    : options;

  const toggle = (value: string) =>
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);

  const allFilteredSelected = filtered.length > 0 && filtered.every((o) => selected.includes(o.value));
  const toggleAll = () => {
    if (allFilteredSelected) {
      const fv = new Set(filtered.map((o) => o.value));
      onChange(selected.filter((v) => !fv.has(v)));
    } else {
      onChange(Array.from(new Set([...selected, ...filtered.map((o) => o.value)])));
    }
  };

  const labelFor = (v: string) => options.find((o) => o.value === v)?.label ?? v;
  const triggerText =
    selected.length === 0
      ? placeholder
      : selected.length <= 2
        ? selected.map(labelFor).join(', ')
        : `${selected.length} נבחרו`;

  return (
    <div className="ms" ref={rootRef}>
      <button
        type="button"
        className={`ms-trigger${selected.length > 0 ? ' has-selection' : ''}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="ms-trigger-text">{triggerText}</span>
        <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <path d="m7 15 5 5 5-5M7 9l5-5 5 5" />
        </svg>
      </button>

      {open && (
        <div className="ms-dropdown">
          <div className="ms-search">
            <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
            />
          </div>

          <div className="ms-toolbar">
            <button type="button" className="ms-selectall" onClick={toggleAll} disabled={filtered.length === 0}>
              {allFilteredSelected ? 'בטל הכל' : 'בחר הכל'}
            </button>
            <span className="ms-count">
              {search && `${filtered.length} תוצאות · `}
              {selected.length > 0 && `${selected.length} נבחרו`}
            </span>
          </div>

          {selected.length > 0 && (
            <div className="ms-chips">
              {selected.map((v) => (
                <button key={v} type="button" className="ms-chip" onClick={() => toggle(v)}>
                  {labelFor(v)}
                  <span aria-hidden>×</span>
                </button>
              ))}
              <button type="button" className="ms-chip-clear" onClick={() => onChange([])}>
                נקה הכל
              </button>
            </div>
          )}

          <div className="ms-options">
            {filtered.map((o) => {
              const isSel = selected.includes(o.value);
              return (
                <label key={o.value} className={`ms-option${isSel ? ' selected' : ''}`}>
                  <input type="checkbox" checked={isSel} onChange={() => toggle(o.value)} />
                  <span className="opt-label">{o.label}</span>
                </label>
              );
            })}
            {filtered.length === 0 && <p className="ms-noresults">לא נמצאו תוצאות</p>}
          </div>
        </div>
      )}
    </div>
  );
}
